import React from "react";
import { ErrorMessage } from "formik";
import { Check } from "lucide-react";
import { Plan } from "../dataTypes";
import { defaultSaasProducts } from "../utils/initialData";

interface FeaturesTabProps {
  values: Plan;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  onPrevious: () => void;
  onNext: () => void;
}

const FeaturesTab: React.FC<FeaturesTabProps> = ({
  values,
  setFieldValue,
  onPrevious,
  onNext,
}) => {
  const toggleSaasProduct = (product: string) => {
    const currentProducts = values.saasProducts;
    const newProducts = currentProducts.includes(product)
      ? currentProducts.filter(p => p !== product)
      : [...currentProducts, product];
      
    setFieldValue('saasProducts', newProducts, true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          SaaS Products *
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Select the products included in this plan. At least one product must be selected.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {defaultSaasProducts.map((product) => (
            <div
              key={product}
              className={`flex items-center justify-between p-5 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                values.saasProducts.includes(product)
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400 shadow-sm"
                  : "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500"
              }`}
              onClick={() => toggleSaasProduct(product)}
            >
              <span className="font-medium text-gray-900 dark:text-white">
                {product}
              </span>
              <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${
                values.saasProducts.includes(product)
                  ? "border-blue-500 bg-blue-500"
                  : "border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-600"
              }`}>
                {values.saasProducts.includes(product) && (
                  <Check className="h-4 w-4 text-white" />
                )}
              </div>
            </div>
          ))}
        </div>
        
        <ErrorMessage name="saasProducts" component="div" className="text-red-500 text-sm mt-3" />
        
        {/* Selected Products Summary */}
        {values.saasProducts.length > 0 && (
          <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
            <p className="text-sm text-green-800 dark:text-green-300">
              <span className="font-semibold">{values.saasProducts.length}</span> product(s) selected
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onPrevious}
          className="px-8 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors duration-200 shadow-sm hover:shadow-md"
        >
          Continue to Trial & Credits
        </button>
      </div>
    </div>
  );
};

export default FeaturesTab;